/**
 * Privacy Policy / Informativa Privacy - Dedicated page (GDPR compliant)
 * Content from Informativa Privacy.rtf – Italian version + English translation
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  AppBar,
  Toolbar,
  Divider,
  Link
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';

interface PrivacyPolicyProps {
  onBack: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: '#003399',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Toolbar>
          <Button
            startIcon={<BackIcon />}
            onClick={onBack}
            sx={{ color: 'white' }}
          >
            Indietro
          </Button>
          <Typography variant="h6" sx={{ ml: 2, color: 'white' }}>
            Informativa Privacy
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, borderRadius: 3 }}>
          {/* Italian version */}
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#003399', mb: 1, textAlign: 'center' }}>
            INFORMATIVA PRIVACY (Privacy Policy)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center', fontStyle: 'italic' }}>
            Versione Italiana – conforme al GDPR
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Ultimo aggiornamento: Gennaio 2026
          </Typography>

          <Divider sx={{ mb: 4 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            Titolare del trattamento
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            <strong>Ing. Francesco Marinuzzi</strong><br />
            Via Vincenzo Tiberio, n. 64 – 00100 Roma (Italia)<br />
            P.IVA: 09728030587<br />
            Email: info@marinuzzi.it
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            Finalità del trattamento
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: '#444' }}>
            I dati personali forniti dagli utenti vengono trattati per le seguenti finalità:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3, color: '#444' }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>registrazione e accesso all&apos;area riservata del sito;</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>gestione dei piani di abbonamento, pagamenti e fatturazione;</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>funzionamento tecnico della piattaforma e miglioramento del servizio;</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>comunicazioni informative e promozionali relative ai servizi offerti;</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>adempimenti legali, fiscali e amministrativi connessi all&apos;attività.</Typography></li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            Base giuridica del trattamento
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: '#444' }}>
            Il trattamento si fonda su:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3, color: '#444' }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>esecuzione di un contratto (art. 6, par. 1, lett. b GDPR);</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>adempimento di obblighi legali (lett. c);</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>consenso dell&apos;interessato per attività promozionali e newsletter (lett. a);</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>legittimo interesse del titolare per sicurezza e prevenzione abusi (lett. f).</Typography></li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            Categorie di dati trattati
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3, color: '#444' }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Dati identificativi (nome, cognome, email);</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Dati di pagamento e fatturazione;</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Dati tecnici di navigazione (indirizzi IP, log, cookie tecnici e di analisi);</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Contenuti generati o caricati dall&apos;utente sulla piattaforma.</Typography></li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            Modalità e luogo del trattamento
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            I dati sono trattati con strumenti elettronici e misure di sicurezza idonee, conservati su server localizzati nell&apos;Unione Europea. Eventuali trasferimenti extra-UE avvengono solo verso soggetti conformi all&apos;art. 45 e ss. GDPR (es. clausole contrattuali standard).
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            Periodo di conservazione
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            I dati vengono conservati per la durata del rapporto contrattuale e, successivamente, per 10 anni per obblighi di legge o fino a revoca del consenso per finalità di marketing.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            Comunicazione e accesso ai dati
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: '#444' }}>
            I dati potranno essere comunicati a:
          </Typography>
          <Box component="ul" sx={{ mb: 2, pl: 3, color: '#444' }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>fornitori di servizi tecnici e IT;</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>istituti bancari e di pagamento;</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>professionisti contabili o legali;</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>autorità competenti per obblighi di legge.</Typography></li>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            Non è previsto alcun trasferimento a terzi per finalità commerciali non autorizzate.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            Diritti dell&apos;interessato
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: '#444' }}>
            L&apos;utente può esercitare in ogni momento i diritti previsti dagli artt. 15-22 GDPR:
          </Typography>
          <Box component="ul" sx={{ mb: 2, pl: 3, color: '#444' }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>accesso, rettifica, cancellazione, limitazione, portabilità e opposizione;</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>revoca del consenso in qualsiasi momento;</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>reclamo al Garante Privacy (<Link href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">www.garanteprivacy.it</Link>).</Typography></li>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            Richieste: info@marinuzzi.it
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            Cookie
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            L&apos;uso dei cookie è disciplinato da un&apos;informativa specifica consultabile alla pagina &quot;Cookie Policy&quot;.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            Aggiornamenti
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: '#444' }}>
            La presente Informativa può essere aggiornata in qualsiasi momento; la versione più recente è pubblicata su <Link href="https://www.erasmus-generator.it" target="_blank" rel="noopener noreferrer">www.erasmus-generator.it</Link>.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            Accettazione
          </Typography>
          <Typography variant="body1" sx={{ mb: 5, lineHeight: 1.8, color: '#444' }}>
            Proseguendo la navigazione o registrandosi al sito, l&apos;utente dichiara di aver letto e compreso la presente Informativa Privacy.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* English translation */}
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#003399', mb: 3, textAlign: 'center' }}>
            PRIVACY POLICY (English Translation)
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            Data Controller
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            Erasmus+ Project Generator – Via Vincenzo Tiberio, n. 64 – 00100 Rome, Italy – VAT: 09728030587 – Email: info@marinuzzi.it
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            <strong>Purposes:</strong> Account registration and login; plan management, billing and payment; technical operation and service improvement; promotional or informational communications; compliance with legal and tax obligations.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            <strong>Legal basis:</strong> Performance of a contract (Art. 6(1)(b) GDPR); legal obligations (c); consent for promotional purposes (a); legitimate interests of the Controller (f).
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            <strong>Categories of data:</strong> Identification data, billing data, technical data (IP, cookies, logs), and user-generated content. Data are stored on EU-based servers. Data are retained for the duration of the contractual relationship and up to 10 years thereafter, or until consent is withdrawn. Data may be shared with IT providers, payment processors, accountants, or legal authorities when required by law. Users may exercise their rights (access, rectify, erase, limit, object) and file complaints to the Italian Data Protection Authority (Garante Privacy). Requests: info@marinuzzi.it. Cookies are governed by the site&apos;s Cookie Policy. This policy may be updated at any time. By using the website, users accept the current version of the Privacy Policy.
          </Typography>

          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Button
              variant="contained"
              onClick={onBack}
              sx={{
                bgcolor: '#003399',
                px: 4,
                py: 1.5,
                '&:hover': { bgcolor: '#002266' }
              }}
            >
              Torna alla home
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
